import React from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, Box, Typography, Chip, LinearProgress } from '@mui/material';

const IssueSection = ({ features, maxFrequency, showQuotes = true }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {features.map((feature, index) => (
      <Box key={index}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {feature.name}
            </Typography>
            <Chip
              label={feature.severity}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.75rem',
                bgcolor: feature.severity === 'high' ? 'grey.100' : 'grey.50',
                color: feature.severity === 'high' ? 'grey.800' : 'grey.600',
                borderColor: feature.severity === 'high' ? 'grey.300' : 'grey.200'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress
              variant="determinate"
              value={(feature.frequency / maxFrequency) * 100}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: 'grey.100',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'grey.600',
                  borderRadius: 1
                }
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 56 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {feature.frequency}
            </Typography>
          </Box>
        </Box>

        {showQuotes && (
          <Box
            sx={{
              mt: 1,
              ml: 2,
              pl: 2,
              borderLeft: '2px solid',
              borderColor: 'grey.200',
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {feature.representative_quotes.map((quote, quoteIndex) => (
              <Typography key={quoteIndex} variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                &quot;{quote}&quot;
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

const NegativeIssues = ({ showQuotes = true }) => {
  const negative_issues = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results.negative_issues);

  if (!negative_issues) return null;

  // Format negative issues
  const issues = Object.entries(negative_issues).map(([name, data]) => ({
    name,
    ...data
  }));

  // Find max frequency for percentage calculation
  const maxFrequency = Math.max(...issues.map((f) => f.frequency));

  // Sort by frequency
  const sortedIssues = [...issues].sort((a, b) => b.frequency - a.frequency);

  return (
    <Card sx={{ width: '100%', mt: 3 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Critical Issues
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Most frequently reported problems and pain points
        </Typography>

        <Box sx={{ mt: 4 }}>
          <IssueSection features={sortedIssues} maxFrequency={maxFrequency} showQuotes={showQuotes} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default NegativeIssues;
